import { Button, Typography } from '@material-tailwind/react';
import {
  FINGER_IMAGE,
  LESSON_SCENE_MAP,
  LessonScene,
  ReadRepeatPhase,
  SentenceScene,
  StartScene,
  STORY_IMAGE,
  StoryScene,
  STORYTELLING_HINT_POINTS,
} from '../../constants/types/free-trial/type';
import {
  ChatBubbleOvalLeftEllipsisIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import FreeTrialAttentionEffect from './FreeTrialAttentionEffect';

interface FreeTrialLessonContentProps {
  currentStep: number;
  sceneIndex: number;
  activityResetKey: number;
  audioProgress: number;
  isAudioPlaying: boolean;
  activityProgress: number;
  isActivityCompleted: boolean;
  meaningStep: number;
  pointRepeatStep: number;
  pointRepeatPartIndex: number;
  pointRepeatIndex: number;
  readRepeatIndex: number;
  readRepeatPhase: ReadRepeatPhase;
  listenRepeatIndex: number;
  listenRepeatPhase: ReadRepeatPhase;
  isStoryHintVisible: boolean;
  storyHintIndex: number;
  isSceneStarted: boolean;
  isPointRepeatPhaseStarted: boolean;
  onStart: () => void;
}

const FreeTrialLessonContent = ({
  currentStep,
  sceneIndex,
  activityResetKey,
  audioProgress,
  isAudioPlaying,
  activityProgress,
  isActivityCompleted,
  meaningStep,
  pointRepeatStep,
  pointRepeatPartIndex,
  pointRepeatIndex,
  readRepeatIndex,
  readRepeatPhase,
  listenRepeatIndex,
  listenRepeatPhase,
  isStoryHintVisible,
  storyHintIndex,
  isSceneStarted,
  isPointRepeatPhaseStarted,
  onStart,
}: FreeTrialLessonContentProps) => {
  const scenes = LESSON_SCENE_MAP[currentStep] ?? [];
  const scene = scenes[sceneIndex];

  if (!scene) return null;

  return (
    <LessonSceneContent
      scene={scene}
      activityResetKey={activityResetKey}
      audioProgress={audioProgress}
      isAudioPlaying={isAudioPlaying}
      activityProgress={activityProgress}
      isActivityCompleted={isActivityCompleted}
      meaningStep={meaningStep}
      pointRepeatStep={pointRepeatStep}
      pointRepeatPartIndex={pointRepeatPartIndex}
      pointRepeatIndex={pointRepeatIndex}
      readRepeatIndex={readRepeatIndex}
      readRepeatPhase={readRepeatPhase}
      listenRepeatIndex={listenRepeatIndex}
      listenRepeatPhase={listenRepeatPhase}
      isStoryHintVisible={isStoryHintVisible}
      storyHintIndex={storyHintIndex}
      isSceneStarted={isSceneStarted}
      isPointRepeatPhaseStarted={isPointRepeatPhaseStarted}
      onStart={onStart}
    />
  );
};

export default FreeTrialLessonContent;

const LessonSceneContent = ({
  scene,
  activityResetKey,
  audioProgress,
  isAudioPlaying,
  isActivityCompleted,
  activityProgress,
  meaningStep,
  pointRepeatStep,
  pointRepeatPartIndex,
  pointRepeatIndex,
  readRepeatIndex,
  readRepeatPhase,
  listenRepeatIndex,
  listenRepeatPhase,
  isStoryHintVisible,
  storyHintIndex,
  isSceneStarted,
  isPointRepeatPhaseStarted,
  onStart,
}: {
  scene: LessonScene;
  activityResetKey: number;
  audioProgress: number;
  isAudioPlaying: boolean;
  activityProgress: number;
  isActivityCompleted: boolean;
  meaningStep: number;
  pointRepeatStep: number;
  pointRepeatPartIndex: number;
  pointRepeatIndex: number;
  readRepeatIndex: number;
  readRepeatPhase: ReadRepeatPhase;
  listenRepeatIndex: number;
  listenRepeatPhase: ReadRepeatPhase;
  isStoryHintVisible: boolean;
  storyHintIndex: number;
  isSceneStarted: boolean;
  isPointRepeatPhaseStarted: boolean;
  onStart: () => void;
}) => {
  if (scene.type === 'START') {
    return <StartLessonScene scene={scene} onStart={onStart} />;
  }

  if (scene.type === 'SECTION_INTRO') {
    return (
      <div className="flex min-h-[300px] flex-col md:min-h-[430px]">
        <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-[#DED8F2] bg-white px-5 py-10 text-center">
          <Typography className="text-[16px] font-bold text-gray-700 md:text-[18px]">
            {scene.title}
          </Typography>

          <Typography className="mt-2 text-[34px] font-black leading-tight text-gray-900 md:text-[42px]">
            {scene.subtitle}
          </Typography>

          {scene.introText && (
            <Typography className="mt-2 text-[20px] font-black text-[#6737F4] md:text-[22px]">
              {scene.introText}
            </Typography>
          )}
        </div>
        <LessonHint>{scene.hint}</LessonHint>
      </div>
    );
  }

  if (scene.type === 'SENTENCE') {
    return (
      <SentenceLessonScene
        scene={scene}
        activityResetKey={activityResetKey}
        audioProgress={audioProgress}
        isAudioPlaying={isAudioPlaying}
        activityProgress={activityProgress}
        isActivityCompleted={isActivityCompleted}
        meaningStep={meaningStep}
        pointRepeatStep={pointRepeatStep}
        pointRepeatPartIndex={pointRepeatPartIndex}
        pointRepeatIndex={pointRepeatIndex}
        readRepeatIndex={readRepeatIndex}
        readRepeatPhase={readRepeatPhase}
        listenRepeatIndex={listenRepeatIndex}
        listenRepeatPhase={listenRepeatPhase}
        isSceneStarted={isSceneStarted}
        isPointRepeatPhaseStarted={isPointRepeatPhaseStarted}
      />
    );
  }

  return (
    <StoryLessonScene
      scene={scene}
      audioProgress={audioProgress}
      isStoryHintVisible={isStoryHintVisible}
      storyHintIndex={storyHintIndex}
      isSceneStarted={isSceneStarted}
    />
  );
};

const StartLessonScene = ({
  scene,
  onStart,
}: {
  scene: StartScene;
  onStart: () => void;
}) => {
  if (scene.mode === 'STORY_INTRO') {
    return (
      <div className="flex min-h-[300px] flex-col md:min-h-[430px]">
        <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-[#DED8F2] bg-white px-5 py-10 text-center">
          <Typography className="text-[16px] font-bold text-gray-700 md:text-[18px]">
            Lesson 2
          </Typography>

          <Typography className="mt-2 text-[34px] font-black leading-tight text-gray-900 md:text-[42px]">
            Today's Story
          </Typography>

          <Typography className="mt-2 text-[20px] font-black text-[#6737F4] md:text-[22px]">
            일곱째 날
          </Typography>
        </div>

        <LessonHint>{scene.hint}</LessonHint>
      </div>
    );
  }

  return (
    <div className="flex min-h-[300px] flex-col md:min-h-[430px]">
      <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-[#DED8F2] bg-white px-5 py-10 text-center">
        <Typography className="text-[18px] font-bold text-gray-700 md:text-[20px]">
          English Story Bible
        </Typography>

        <Typography className="mt-3 text-[34px] font-black leading-tight text-gray-900 md:text-[42px]">
          Book 1
        </Typography>

        <Typography className="mt-1 text-[34px] font-black leading-tight text-gray-900 md:text-[42px]">
          In the <span className="text-red-500">beginning</span>
        </Typography>

        {scene.mode === 'READY' && (
          <div className="relative mt-8 inline-flex rounded-full pb-[5px]">
            <FreeTrialAttentionEffect />

            <Button
              ripple={false}
              onClick={onStart}
              className="relative z-10 flex h-12 items-center gap-2 rounded-full bg-[#6737F4] px-7 py-0 text-[14px] font-bold normal-case text-white !shadow-[0_5px_0_#4D24C7]"
            >
              <PlayIcon className="h-4 w-4 fill-current" />
              수업 시작하기
            </Button>
          </div>
        )}
      </div>

      <LessonHint>{scene.hint}</LessonHint>
    </div>
  );
};

const SentenceLessonScene = ({
  scene,
  activityResetKey,
  audioProgress,
  isAudioPlaying,
  activityProgress,
  isActivityCompleted,
  meaningStep,
  pointRepeatStep,
  pointRepeatPartIndex,
  pointRepeatIndex,
  readRepeatIndex,
  readRepeatPhase,
  listenRepeatIndex,
  listenRepeatPhase,
  isSceneStarted,
  isPointRepeatPhaseStarted,
}: {
  scene: SentenceScene;
  activityResetKey: number;
  audioProgress: number;
  isAudioPlaying: boolean;
  activityProgress: number;
  isActivityCompleted: boolean;
  meaningStep: number;
  pointRepeatStep: number;
  pointRepeatPartIndex: number;
  pointRepeatIndex: number;
  readRepeatIndex: number;
  readRepeatPhase: ReadRepeatPhase;
  listenRepeatIndex: number;
  listenRepeatPhase: ReadRepeatPhase;
  isSceneStarted: boolean;
  isPointRepeatPhaseStarted: boolean;
}) => {
  const [isPointFingerResetting, setIsPointFingerResetting] = useState(false);

  const isReadRepeat = scene.activity === 'READ_REPEAT';
  const isListenRepeat = scene.activity === 'LISTEN_REPEAT';
  const isPointRepeat = scene.activity === 'POINT_REPEAT';

  const isPointRepeatListening = pointRepeatStep === 2 || pointRepeatStep === 4;

  useEffect(() => {
    if (!isPointRepeat || !isPointRepeatPhaseStarted) {
      return;
    }

    setIsPointFingerResetting(true);

    const timer = window.setTimeout(() => {
      setIsPointFingerResetting(false);
    }, 150);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    isPointRepeat,
    isPointRepeatPhaseStarted,
    isPointRepeatListening,
    pointRepeatPartIndex,
  ]);

  const pointRepeatParts = isPointRepeat ? (scene.pointRepeatParts ?? []) : [];

  const currentPointRepeatPart = pointRepeatParts[pointRepeatPartIndex];

  const currentPointRepeatPoint =
    currentPointRepeatPart?.points[pointRepeatIndex];

  const activePointRepeatPoint = (() => {
    if (!currentPointRepeatPart) {
      return undefined;
    }

    const isListening = pointRepeatStep === 2 || pointRepeatStep === 4;

    const isRepeating = pointRepeatStep === 5;

    if (!isListening && !isRepeating) {
      return currentPointRepeatPoint;
    }

    const progress = isRepeating ? activityProgress : audioProgress;

    let activePoint = currentPointRepeatPart.points[0];

    currentPointRepeatPart.points.forEach((point) => {
      if (progress >= point.cueProgress) {
        activePoint = point;
      }
    });

    return activePoint;
  })();

  const isMeaning = scene.activity === 'MEANING';

  const meaningGroups =
    scene.meaningGroups ??
    (scene.meaningItems?.length ? [scene.meaningItems] : []);

  const totalMeaningChunks = meaningGroups.flat().length;

  const isMeaningCompleted =
    totalMeaningChunks > 0 && meaningStep > totalMeaningChunks;

  const activeMeaningGroupIndex = (() => {
    if (!isMeaning || meaningGroups.length === 0) {
      return 0;
    }

    const currentStep = Math.min(Math.max(meaningStep, 1), totalMeaningChunks);

    let accumulated = 0;

    for (let index = 0; index < meaningGroups.length; index += 1) {
      accumulated += meaningGroups[index].length;

      if (currentStep <= accumulated) {
        return index;
      }
    }

    return meaningGroups.length - 1;
  })();

  const isRepeating =
    (isReadRepeat || isListenRepeat) &&
    !isAudioPlaying &&
    audioProgress >= 100 &&
    !isActivityCompleted;

  const showPointFinger =
    isPointRepeatPhaseStarted &&
    isPointRepeat &&
    !isPointFingerResetting &&
    [0, 1, 2, 4, 5].includes(pointRepeatStep);

  const showReadRepeatBadge = isReadRepeat && readRepeatPhase === 'REPEAT';

  const showPointRepeatBadge =
    isPointRepeatPhaseStarted && isPointRepeat && pointRepeatStep === 5;

  const showListenRepeatBadge =
    isListenRepeat && listenRepeatPhase === 'REPEAT';

  const isListenRepeatCompleted =
    isListenRepeat && listenRepeatPhase === 'DONE';

  const activeFingerMotion = activePointRepeatPoint?.fingerMotion;

  const shouldUseOrbit =
    activeFingerMotion?.type === 'ORBIT' &&
    [0, 1, 2, 4, 5].includes(pointRepeatStep);
  const fingerOrbitKey = `${pointRepeatPartIndex}-${pointRepeatIndex}-${pointRepeatStep === 1 ? 0 : pointRepeatStep}`;

  const isFingerOrbitRunning = pointRepeatStep === 5 || isAudioPlaying;

  const fingerOrbitDuration =
    pointRepeatStep === 5
      ? (scene.durationSeconds ?? 3)
      : (activeFingerMotion?.durationSeconds ?? 2);

  return (
    <div className="flex min-h-[300px] flex-col md:min-h-[430px]">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-[#DED8F2] bg-white">
        <img
          src={STORY_IMAGE}
          alt="일곱째 날 성경 이야기"
          className="absolute inset-0 h-full w-full object-cover"
        />

        {/* 상단 영역 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[19%]"
          style={{
            backgroundColor: `rgba(255,255,255,${scene.dim[0]})`,
          }}
        />

        {/* 가운데 영역 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-[19%] h-[64%]"
          style={{
            backgroundColor: `rgba(255,255,255,${scene.dim[1]})`,
          }}
        />

        {/* 하단 영역 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[17%]"
          style={{
            backgroundColor: `rgba(255,255,255,${scene.dim[2]})`,
          }}
        />

        {/* TALK 진입 / 다시 */}
        {scene.activity === 'TALK' && (
          <div
            key={activityResetKey}
            aria-hidden
            className="animate-free-trial-flash pointer-events-none absolute inset-x-0 top-0 z-10 h-[19%] bg-white"
          />
        )}

        {/* TALK */}
        {scene.activity === 'TALK' && (
          <div className="animate-free-trail-float absolute left-4 top-4 z-20 flex items-center gap-1.5 rounded-full bg-[#FFD54A] px-3 py-1.5 !shadow-[0_4px_0_#D5A900]">
            <ChatBubbleOvalLeftEllipsisIcon className="h-4 w-4 stroke-[2.5] text-gray-900" />

            <span className="text-[11px] font-extrabold text-gray-900 md:text-[12px]">
              우리말로 이야기해요
            </span>
          </div>
        )}

        {/* READ_REPEAT */}
        {(showReadRepeatBadge || showListenRepeatBadge) && (
          <div className="animate-free-trail-float absolute left-4 top-4 z-20 flex items-center gap-1.5 rounded-full bg-[#FFD54A] px-3 py-1.5 !shadow-[0_4px_0_#D5A900]">
            <ChatBubbleOvalLeftEllipsisIcon className="h-4 w-4 stroke-[2.5] text-gray-900" />

            <span className="text-[11px] font-extrabold text-gray-900 md:text-[12px]">
              따라 하세요!
            </span>
          </div>
        )}

        {/* POINT_REPEAT */}
        {showPointRepeatBadge && (
          <div className="animate-free-trail-float absolute left-4 top-4 z-20 flex items-center gap-1.5 rounded-full bg-[#FFD54A] px-3 py-1.5 !shadow-[0_4px_0_#D5A900]">
            <ChatBubbleOvalLeftEllipsisIcon className="h-4 w-4 stroke-[2.5] text-gray-900" />

            <span className="text-[11px] font-extrabold text-gray-900 md:text-[12px]">
              짚으며 따라 하세요!
            </span>
          </div>
        )}

        {/* 문장 */}
        {scene.showSentence !== false && (
          <div
            className={`${scene.textClassName} text-[clamp(18px,3.8vw,40px)]`}
          >
            {scene.lines.map((line, index) => {
              const isHiddenReadRepeatLine =
                isReadRepeat &&
                readRepeatPhase !== 'DONE' &&
                index > readRepeatIndex;

              const isHiddenMeaningLine =
                isMeaning &&
                !isMeaningCompleted &&
                index > activeMeaningGroupIndex;

              const isHiddenListenRepeatLine =
                isListenRepeat &&
                !isListenRepeatCompleted &&
                index > listenRepeatIndex;

              const isHiddenPointRepeatLine =
                isPointRepeat &&
                pointRepeatParts.length > 1 &&
                pointRepeatPartIndex === 0 &&
                index > 0;

              const shouldAnimateReadRepeat =
                isReadRepeat &&
                readRepeatIndex > 0 &&
                readRepeatPhase !== 'DONE' &&
                index === readRepeatIndex;

              const shouldAnimateMeaning =
                isMeaning &&
                activeMeaningGroupIndex > 0 &&
                !isMeaningCompleted &&
                index === activeMeaningGroupIndex;

              const shouldAnimateListenRepeat =
                isListenRepeat &&
                listenRepeatIndex > 0 &&
                listenRepeatPhase !== 'DONE' &&
                index === listenRepeatIndex;

              const isHiddenLine =
                isHiddenReadRepeatLine ||
                isHiddenMeaningLine ||
                isHiddenListenRepeatLine ||
                isHiddenPointRepeatLine;

              const shouldAnimate =
                shouldAnimateReadRepeat ||
                shouldAnimateMeaning ||
                shouldAnimateListenRepeat;

              return (
                <div
                  key={index}
                  className={`text-gray-950 font-black leading-[0.87] ${isHiddenLine ? 'invisible' : ''} ${shouldAnimate ? 'animate-free-trial-slide-up' : ''}`}
                >
                  {line.map((segment, segmentIndex) => (
                    <span
                      key={`${segment.text}-${segmentIndex}`}
                      className={
                        segment.highlight ? 'text-red-500' : 'text-gray-950'
                      }
                    >
                      {segment.text}
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {isPointRepeatPhaseStarted &&
          isPointRepeat &&
          (pointRepeatStep === 0 || pointRepeatStep === 1) &&
          currentPointRepeatPoint && (
            <div
              className={`absolute z-20 ${
                scene.pointRepeatGuideClassName ??
                'bottom-[4%] left-1/2 -translate-x-1/2'
              }`}
            >
              <div className="flex items-center gap-2 rounded-2xl border-2 border-[#A996F6] bg-white px-4 py-2 shadow-sm">
                <span className="flex h-5 items-center whitespace-nowrap text-[12px] font-black leading-none text-red-500 md:text-[13px]">
                  {currentPointRepeatPoint.text}
                </span>

                <span className="flex h-5 items-center whitespace-nowrap text-[10px] font-extrabold leading-none text-[#6737F4] md:text-[11px]">
                  {currentPointRepeatPoint.meaning}
                </span>

                <span className="flex h-5 items-center leading-none text-gray-700">
                  →
                </span>

                <span className="flex h-5 items-center whitespace-nowrap text-[10px] font-bold leading-none text-gray-700 md:text-[11px]">
                  {currentPointRepeatPoint.action}
                </span>
              </div>
            </div>
          )}

        {/* 포인터 */}
        {isPointRepeat
          ? showPointFinger &&
            activePointRepeatPoint &&
            (shouldUseOrbit && activeFingerMotion ? (
              <div
                key={fingerOrbitKey}
                className="pointer-events-none absolute inset-0 z-30"
              >
                {/* 이 레이어 전체가 7을 중심으로 회전 */}
                <div
                  className="animate-free-trial-finger-orbit absolute inset-0"
                  style={{
                    transformOrigin: `${activeFingerMotion.centerX}% ${activeFingerMotion.centerY}%`,
                    animationDuration: `${fingerOrbitDuration}s`,
                    animationPlayState: isFingerOrbitRunning
                      ? 'running'
                      : 'paused',
                  }}
                >
                  <img
                    src={FINGER_IMAGE}
                    alt=""
                    aria-hidden
                    className="animate-free-trial-finger-counter-orbit absolute w-[8%] drop-shadow-sm"
                    style={{
                      left: `${activeFingerMotion.centerX}%`,

                      // 7 중심에서 원의 반지름만큼 위에서 시작
                      top: `${activeFingerMotion.centerY - activeFingerMotion.radius}%`,

                      animationDuration: `${fingerOrbitDuration}s`,
                      animationPlayState: isFingerOrbitRunning
                        ? 'running'
                        : 'paused',
                    }}
                  />
                </div>
              </div>
            ) : (
              <img
                src={FINGER_IMAGE}
                alt=""
                aria-hidden
                className={`pointer-events-none absolute w-[8%] drop-shadow-sm transition-[left,top] duration-500 ease-in-out ${activePointRepeatPoint.finger.className}`}
              />
            ))
          : scene.fingers.map((finger, index) => (
              <img
                key={index}
                src={FINGER_IMAGE}
                alt=""
                aria-hidden
                className={`pointer-events-none absolute w-[8%] drop-shadow-sm ${finger.className}`}
              />
            ))}
      </div>

      <SentenceHint
        scene={scene}
        meaningStep={meaningStep}
        readRepeatIndex={readRepeatIndex}
        readRepeatPhase={readRepeatPhase}
        listenRepeatIndex={listenRepeatIndex}
        listenRepeatPhase={listenRepeatPhase}
        pointRepeatPartIndex={pointRepeatPartIndex}
      />
    </div>
  );
};

const StoryLessonScene = ({
  scene,
  audioProgress,
  isStoryHintVisible,
  storyHintIndex,
  isSceneStarted,
}: {
  scene: StoryScene;
  audioProgress: number;
  isStoryHintVisible: boolean;
  storyHintIndex: number;
  isSceneStarted: boolean;
}) => {
  const storytellingHintPoint =
    scene.mode === 'STORYTELLING' && isStoryHintVisible
      ? STORYTELLING_HINT_POINTS[storyHintIndex]
      : undefined;

  const activeReviewCue = (() => {
    if (
      scene.mode !== 'WATCH' ||
      !isSceneStarted ||
      !scene.reviewCues?.length
    ) {
      return undefined;
    }

    let activeCue = scene.reviewCues[0];

    scene.reviewCues.forEach((cue) => {
      if (audioProgress >= cue.cueProgress) {
        activeCue = cue;
      }
    });

    return activeCue;
  })();

  const dim: [number, number, number] =
    activeReviewCue?.dim ?? (scene.faded ? [0.78, 0.78, 0.78] : [0, 0, 0]);

  const hintSegments = activeReviewCue?.hint;

  return (
    <div className="flex min-h-[300px] flex-col md:min-h-[430px]">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-[#DED8F2] bg-white">
        <img
          src={STORY_IMAGE}
          alt="일곱째 날 성경 이야기"
          className="absolute inset-0 h-full w-full object-cover"
        />

        {/* 상단 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[19%] transition-colors duration-500 ease-out"
          style={{
            backgroundColor: `rgba(255,255,255,${dim[0]})`,
          }}
        />

        {/* 가운데 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-[19%] h-[64%] transition-colors duration-500 ease-out"
          style={{
            backgroundColor: `rgba(255,255,255,${dim[1]})`,
          }}
        />

        {/* 하단 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[17%] transition-colors duration-500 ease-out"
          style={{
            backgroundColor: `rgba(255,255,255,${dim[2]})`,
          }}
        />

        {scene.mode === 'STORYTELLING' && storytellingHintPoint ? (
          storytellingHintPoint.fingerMotion?.type === 'ORBIT' ? (
            <div
              key={`storytelling-orbit-${storyHintIndex}`}
              className="pointer-events-none absolute inset-0 z-30"
            >
              <div
                className="animate-free-trial-finger-orbit absolute inset-0"
                style={{
                  transformOrigin: `${storytellingHintPoint.fingerMotion.centerX}% ${storytellingHintPoint.fingerMotion.centerY}%`,
                  animationDuration: `${storytellingHintPoint.fingerMotion.durationSeconds}s`,
                }}
              >
                <img
                  src={FINGER_IMAGE}
                  alt=""
                  aria-hidden
                  className="animate-free-trial-finger-counter-orbit absolute w-[8%] drop-shadow-sm"
                  style={{
                    left: `${storytellingHintPoint.fingerMotion.centerX}%`,
                    top: `${
                      storytellingHintPoint.fingerMotion.centerY -
                      storytellingHintPoint.fingerMotion.radius
                    }%`,
                    animationDuration: `${storytellingHintPoint.fingerMotion.durationSeconds}s`,
                  }}
                />
              </div>
            </div>
          ) : (
            <img
              src={FINGER_IMAGE}
              alt=""
              aria-hidden
              className={`pointer-events-none absolute z-30 w-[8%] drop-shadow-sm transition-[left,top] duration-500 ease-in-out ${storytellingHintPoint.finger.className}`}
            />
          )
        ) : (
          scene.mode !== 'STORYTELLING' &&
          scene.fingers?.map((finger, index) => (
            <img
              key={index}
              src={FINGER_IMAGE}
              alt=""
              aria-hidden
              className={`pointer-events-none absolute w-[8%] drop-shadow-sm ${finger.className}`}
            />
          ))
        )}
      </div>

      {scene.mode === 'STORYTELLING' && isStoryHintVisible ? (
        <LessonHint>
          힌트 {storyHintIndex + 1} / {STORYTELLING_HINT_POINTS.length} ·
          손가락이 가리키는 곳을 짚으며 말해요
        </LessonHint>
      ) : hintSegments ? (
        <div className="mt-2 flex min-h-[35px] items-center justify-center rounded-xl border border-[#DED8F2] bg-white px-4 py-2 md:mt-3 md:min-h-[52px]">
          <div className="text-center text-[14px] font-black md:text-[15px]">
            {hintSegments.map((segment, index) => (
              <span
                key={`${segment.text}-${index}`}
                className={segment.highlight ? 'text-red-500' : 'text-gray-900'}
              >
                {segment.text}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <LessonHint>{scene.hint}</LessonHint>
      )}
    </div>
  );
};

const LessonHint = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="mt-2 flex min-h-[35px] items-center justify-center rounded-xl border border-[#DED8F2] bg-white px-4 py-2 md:mt-3 md:min-h-[52px]">
      <Typography className="text-[10px] font-medium text-gray-700 md:text-[11px]">
        {children}
      </Typography>
    </div>
  );
};

const SentenceHint = ({
  scene,
  meaningStep,
  readRepeatIndex,
  readRepeatPhase,
  listenRepeatIndex,
  listenRepeatPhase,
  pointRepeatPartIndex,
}: {
  scene: SentenceScene;
  meaningStep: number;
  readRepeatIndex: number;
  readRepeatPhase: ReadRepeatPhase;
  listenRepeatIndex: number;
  listenRepeatPhase: ReadRepeatPhase;
  pointRepeatPartIndex: number;
}) => {
  if (scene.activity === 'TALK' || scene.activity === 'LISTEN') {
    return <LessonHint>{scene.hint}</LessonHint>;
  }

  if (scene.activity === 'MEANING') {
    const meaningGroups =
      scene.meaningGroups ??
      (scene.meaningItems?.length ? [scene.meaningItems] : []);

    const totalChunks = meaningGroups.flat().length;

    const isCompleted = totalChunks > 0 && meaningStep > totalChunks;

    const currentStep = Math.min(Math.max(meaningStep, 1), totalChunks);

    let accumulated = 0;
    let activeGroupIndex = 0;
    let activeItemIndex = 0;

    for (let index = 0; index < meaningGroups.length; index += 1) {
      const group = meaningGroups[index];

      if (currentStep <= accumulated + group.length) {
        activeGroupIndex = index;
        activeItemIndex = currentStep - accumulated - 1;
        break;
      }

      accumulated += group.length;
    }

    const activeGroup = meaningGroups[activeGroupIndex] ?? [];

    const visibleMeaningGroups = isCompleted ? meaningGroups : [activeGroup];

    const highlightedWords = new Set(
      scene.lines
        .flat()
        .filter((segment) => segment.highlight)
        .flatMap((segment) => segment.text.trim().split(/\s+/).filter(Boolean)),
    );

    return (
      <div className="mt-2 flex min-h-[35px] items-center justify-center rounded-xl border border-[#DED8F2] bg-white px-4 py-2 md:mt-3 md:min-h-[52px]">
        <div className="flex flex-col items-center justify-center gap-2 md:flex-row">
          {visibleMeaningGroups.map((group, groupIndex) => (
            <div
              key={groupIndex}
              className="flex items-center justify-center gap-2"
            >
              {group.map((item, index) => {
                const isActive =
                  meaningStep > 0 && !isCompleted && activeItemIndex === index;

                return (
                  <div
                    key={item.text}
                    className={`rounded-lg border px-3 py-1.5 text-center transition-all duration-150 ease-out ${
                      isActive
                        ? '-translate-y-0.5 border-[#F2B829] bg-[#FFF8DA]'
                        : 'translate-y-0 border-transparent bg-[#F5F2FF]'
                    }`}
                  >
                    <div className="whitespace-nowrap text-[12px] font-black md:text-[13px]">
                      {item.text.split(' ').map((word, wordIndex) => (
                        <span key={`${word}-${wordIndex}`}>
                          {wordIndex > 0 && ' '}

                          <span
                            className={
                              highlightedWords.has(word)
                                ? 'text-red-500'
                                : 'text-gray-900'
                            }
                          >
                            {word}
                          </span>
                        </span>
                      ))}

                      <span className="text-gray-900">{item.punctuation}</span>
                    </div>

                    <div className="mt-0.5 whitespace-nowrap text-[9px] font-extrabold text-[#6737F4] md:text-[10px]">
                      {item.meaning}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (scene.activity === 'READ_REPEAT') {
    const isCompleted = readRepeatPhase === 'DONE';

    const hintLines = isCompleted
      ? scene.lines
      : scene.lines.slice(readRepeatIndex, readRepeatIndex + 1);

    return (
      <div className="mt-2 flex min-h-[35px] items-center justify-center rounded-xl border border-[#DED8F2] bg-white px-4 py-2 md:mt-3 md:min-h-[52px]">
        <div className="text-center text-[14px] font-black md:text-[15px]">
          {hintLines.map((line, lineIndex) => (
            <span key={lineIndex}>
              {lineIndex > 0 && ' '}

              {line.map((segment, segmentIndex) => (
                <span
                  key={`${segment.text}-${segmentIndex}`}
                  className={
                    segment.highlight ? 'text-red-500' : 'text-gray-900'
                  }
                >
                  {segment.text}
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (scene.activity === 'LISTEN_REPEAT') {
    const isCompleted = listenRepeatPhase === 'DONE';

    const hintLines = isCompleted
      ? scene.lines
      : scene.lines.slice(listenRepeatIndex, listenRepeatIndex + 1);

    return (
      <div className="mt-2 flex min-h-[35px] items-center justify-center rounded-xl border border-[#DED8F2] bg-white px-4 py-2 md:mt-3 md:min-h-[52px]">
        <div className="text-center text-[14px] font-black md:text-[15px]">
          {hintLines.map((line, lineIndex) => (
            <span key={lineIndex}>
              {lineIndex > 0 && ' '}

              {line.map((segment, segmentIndex) => (
                <span
                  key={`${segment.text}-${segmentIndex}`}
                  className={
                    segment.highlight ? 'text-red-500' : 'text-gray-900'
                  }
                >
                  {segment.text}
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (scene.activity === 'POINT_REPEAT') {
    const pointRepeatParts = scene.pointRepeatParts ?? [];

    const hintLines =
      pointRepeatParts.length > 1 && pointRepeatPartIndex === 0
        ? scene.lines.slice(0, 1)
        : scene.lines;

    return (
      <div className="mt-2 flex min-h-[35px] items-center justify-center rounded-xl border border-[#DED8F2] bg-white px-4 py-2 md:mt-3 md:min-h-[52px]">
        <div className="text-center text-[14px] font-black md:text-[15px]">
          {hintLines.map((line, lineIndex) => (
            <span key={lineIndex}>
              {lineIndex > 0 && ' '}

              {line.map((segment, segmentIndex) => (
                <span
                  key={`${segment.text}-${segmentIndex}`}
                  className={
                    segment.highlight ? 'text-red-500' : 'text-gray-900'
                  }
                >
                  {segment.text}
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 flex min-h-[35px] items-center justify-center rounded-xl border border-[#DED8F2] bg-white px-4 py-2 md:mt-3 md:min-h-[52px]">
      <div className="text-center text-[14px] font-black md:text-[15px]">
        {scene.lines.flat().map((segment, index) => (
          <span
            key={`${segment.text}-${index}`}
            className={segment.highlight ? 'text-red-500' : 'text-gray-900'}
          >
            {segment.text}
          </span>
        ))}
      </div>
    </div>
  );
};
